import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => `UGX ${Number(v || 0).toLocaleString()}`;

// Live sponsorship financial overview — a filtered view of Finance income/expenses.
export const FinanceOverviewCards = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null); // 'income' | 'expense'
  const [txs, setTxs] = useState([]);
  const [filters, setFilters] = useState({ start_date: '', end_date: '', reference: '', sponsor: '' });

  const load = useCallback(() => {
    axios.get(`${API}/api/sponsorship/finance-overview`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const openDrill = async (type, f = filters) => {
    setDrill(type);
    const params = new URLSearchParams({ tx_type: type });
    Object.entries(f).forEach(([k, v]) => v && params.append(k, v));
    try {
      const r = await axios.get(`${API}/api/sponsorship/finance-transactions?${params}`);
      setTxs(r.data.items || []);
    } catch { setTxs([]); }
  };

  if (!data) return null;
  const cards = [
    { label: 'TOTAL INCOME', value: data.total_income, cls: 'text-green-700', click: () => openDrill('income'), testid: 'sp-card-income' },
    { label: 'TOTAL EXPENDITURE', value: data.total_expenditure, cls: 'text-red-600', click: () => openDrill('expense'), testid: 'sp-card-expenditure' },
    { label: 'CURRENT BALANCE', value: data.current_balance, cls: data.current_balance >= 0 ? 'text-primary' : 'text-red-600', testid: 'sp-card-balance' },
    { label: 'Sponsored Children', value: data.sponsored_children, raw: true, cls: '', testid: 'sp-card-children' },
    { label: 'Total Donations Received', value: data.total_donations, cls: 'text-green-700', click: () => openDrill('income'), testid: 'sp-card-donations' },
    { label: 'Allocated to Children', value: data.total_allocated_to_children, cls: '', testid: 'sp-card-allocated' },
  ];

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4" data-testid="sponsorship-finance-overview">
        {cards.map((c) => (
          <Card key={c.label} className={c.click ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} onClick={c.click} data-testid={c.testid}>
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground">{c.label}</p>
              <p className={`text-sm lg:text-base font-heading font-bold tabular-nums ${c.cls}`}>{c.raw ? c.value : fmt(c.value)}</p>
              {c.click && <p className="text-[9px] text-muted-foreground">click to view transactions</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="sp-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Sponsorship {drill === 'income' ? 'Income' : 'Expenditure'} — underlying Finance transactions</DialogTitle>
            <DialogDescription>One source of truth: these are the actual Finance records tagged to the Sponsorship Program.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2">
            <Input type="date" value={filters.start_date} onChange={(e) => { const f = { ...filters, start_date: e.target.value }; setFilters(f); openDrill(drill, f); }} data-testid="sp-filter-start" />
            <Input type="date" value={filters.end_date} onChange={(e) => { const f = { ...filters, end_date: e.target.value }; setFilters(f); openDrill(drill, f); }} data-testid="sp-filter-end" />
            <Input placeholder="Reference no." value={filters.reference} onChange={(e) => { const f = { ...filters, reference: e.target.value }; setFilters(f); openDrill(drill, f); }} data-testid="sp-filter-ref" />
            <Input placeholder="Sponsor" value={filters.sponsor} onChange={(e) => { const f = { ...filters, sponsor: e.target.value }; setFilters(f); openDrill(drill, f); }} data-testid="sp-filter-sponsor" />
          </div>
          <table className="w-full">
            <thead><tr>{['Date', drill === 'income' ? 'Source' : 'Category', 'Description', 'Child', 'Reference', 'Amount'].map((h) => <th key={h} className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase bg-muted border-b">{h}</th>)}</tr></thead>
            <tbody>
              {txs.map((t) => (
                <tr key={t.id} data-testid={`sp-tx-${t.id}`}>
                  <td className="px-2 py-1.5 border-b text-xs">{t.date}</td>
                  <td className="px-2 py-1.5 border-b text-xs capitalize">{((drill === 'income' ? t.source : t.subcategory || t.category) || '—').replace(/_/g, ' ')}</td>
                  <td className="px-2 py-1.5 border-b text-xs">{t.description || '—'}</td>
                  <td className="px-2 py-1.5 border-b text-xs">{t.child_name || '—'}</td>
                  <td className="px-2 py-1.5 border-b text-xs font-mono text-[10px]">{t.reference_number || t.id.slice(0, 8)}</td>
                  <td className="px-2 py-1.5 border-b text-xs text-right tabular-nums">{fmt(t.amount)}</td>
                </tr>
              ))}
              {txs.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-muted-foreground text-sm">No transactions match</td></tr>}
            </tbody>
          </table>
          <p className="text-right text-sm font-semibold">Total: {fmt(txs.reduce((a, t) => a + (t.amount || 0), 0))}</p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FinanceOverviewCards;
